import {DialogTitle} from '@headlessui/react'
import {useTranslation} from 'react-i18next'
import {twJoin} from 'tailwind-merge'

import {IconArrowLeftNav, IconCross, IconPlaceholder} from '@sdox/icons'

import {ResponsiveButton} from '../Button'
import styles from './SlideoutHeader.module.scss'

import type {SlideoutVariants} from './types'
import type {FixedArray} from '@sdox/utils'
import type {ButtonProps} from '../Button'
import type {DialogProps} from '@headlessui/react'

export interface SlideoutHeaderProps extends Partial<Pick<DialogProps, 'onClose'>> {
  hasDialogParent?: boolean
  heading?: string
  isStacked?: boolean
  trailingCTAs?: FixedArray<ButtonProps, 1 | 2>
  sticky?: boolean
  surface?: boolean
  variant?: SlideoutVariants
}

const titleClasses: Record<SlideoutVariants, string> = {
  'custom': '',
  'content': styles.titleContent,
  'crud-eufe': styles.titleCrudEufe,
  'crud-cufe': styles.titleCrudCufe,
}

const containerClasses: Record<SlideoutVariants, string> = {
  'custom': '',
  'content': styles.containerContent,
  'crud-eufe': styles.containerCrudEufe,
  'crud-cufe': styles.containerCrudCufe,
}

const rowClasses: Record<SlideoutVariants, string> = {
  'custom': '',
  'content': styles.rowContent,
  'crud-eufe': styles.rowCrudEufe,
  'crud-cufe': styles.rowCrudCufe,
}

export const SlideoutHeader = (props: SlideoutHeaderProps) => {
  const {
    hasDialogParent = true,
    heading,
    variant = 'content',
    sticky = false,
    isStacked = false,
    surface = false,
    onClose,
    trailingCTAs,
  } = props
  const {t} = useTranslation()

  const Title = hasDialogParent ? DialogTitle : 'h2'

  const titleElement = (
    <Title className={titleClasses[variant]}>
      {heading}
    </Title>
  )

  const buttonSizes: Record<string, ButtonProps> = variant === 'crud-cufe' ? {
    small: {size: 'small'},
  } : {
    small: {
      className: 'inline-flex @[664px]:hidden',
      size: 'small',
    },
    medium: {
      className: 'hidden @[664px]:inline-flex',
      size: 'medium',
    },
  }

  const CTAs = trailingCTAs?.map((props, index) => (
    index < 2 ? (
      <ResponsiveButton
        key={index}
        uniqueProps={buttonSizes}
        commonProps={{
          leading: <IconPlaceholder />,
          variant: 'filled-inversed',
          ...props,
        }}
      />
    ) : null
  ))

  const noButtons = !trailingCTAs && !onClose

  return (
    <div
      className={twJoin(
        'flex flex-col',
        sticky && 'absolute z-50 top-0 inset-x-0',
        surface && 'bg-surface-primary',
        containerClasses[variant],
      )}
    >
      <div
        className={twJoin(
          'flex items-center',
          rowClasses[variant],
          noButtons && 'hidden',
        )}
      >
        {onClose ? (
          <ResponsiveButton
            uniqueProps={buttonSizes}
            commonProps={{
              'aria-label': t('common:close'),
              'title': t('common:close'),
              onClick: () => onClose(false),
              leading: isStacked ? <IconArrowLeftNav /> : <IconCross />,
              variant: 'filled-inversed',
            }}
          />
        ) : null}
        {variant === 'content' ? titleElement : <div className={'grow'}></div>}
        <div className={'flex gap-spacing-8'}>
          {CTAs}
        </div>
      </div>
      {variant !== 'content' ? titleElement : null}
    </div>
  )
}
